/**
 * Modal para editar usuarios del sistema
 *
 * En el nuevo modelo:
 * - Los usuarios se registran solos con email/password
 * - Las asociaciones pueden actualizar información y asignar roles
 * - No se crean usuarios manualmente (excepto el primer admin)
 */

import { useEffect } from "react";
import { X, User, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useUpdateSystemUser } from "@/hooks/useUsers";
import { useAuthStore } from "@/stores/authStore";
import { type SystemUser } from "@/types";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: SystemUser | null;
  /** @deprecated No longer used in new auth model */
  onPasswordCreated?: (username: string, password: string) => void;
}

const userSchema = z.object({
  displayName: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  phone: z.string().optional(),
  status: z.enum(["active", "inactive", "pending"]),
});

type UserFormData = z.infer<typeof userSchema>;

export default function UserModal({ isOpen, onClose, user }: UserModalProps) {
  const { profile } = useAuthStore();
  const updateMutation = useUpdateSystemUser();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      displayName: "",
      phone: "",
      status: "active",
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen && user) {
      reset({
        displayName: user.displayName,
        phone: user.phone || "",
        status: user.status,
      });
    } else {
      reset({
        displayName: "",
        phone: "",
        status: "active",
      });
    }
  }, [isOpen, user, reset]);

  const onSubmit = async (data: UserFormData) => {
    if (!user) return;

    try {
      await updateMutation.mutateAsync({
        userId: user.id,
        data: {
          displayName: data.displayName,
          phone: data.phone || undefined,
          status: data.status,
        },
        updatedBy: profile?.email || "system",
      });
      onClose();
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Error al actualizar el usuario");
    }
  };

  if (!isOpen) return null;

  // Si no hay usuario, no se puede editar
  if (!user) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Nuevo Usuario
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              En el nuevo modelo de autenticación, los usuarios se registran con
              su propio email. Los administradores pueden asignar roles desde la
              página de gestión de usuarios.
            </p>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={onClose} className="btn btn-secondary">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-md shadow-blue-500/25">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Editar Usuario
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Email (readonly) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={user.email}
              readOnly
              className="input w-full bg-gray-50 dark:bg-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">
              El email no se puede cambiar
            </p>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nombre Completo *
            </label>
            <input
              type="text"
              {...register("displayName")}
              placeholder="Juan Pérez García"
              className="input w-full"
            />
            {errors.displayName && (
              <p className="text-sm text-red-500 mt-1">
                {errors.displayName.message}
              </p>
            )}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Teléfono
            </label>
            <input
              type="tel"
              {...register("phone")}
              placeholder="+57 300 123 4567"
              className="input w-full"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Estado
            </label>
            <select {...register("status")} className="input w-full">
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
              <option value="pending">Pendiente</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center gap-2"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                "Guardar Cambios"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
